import { defineStore } from 'pinia'
import { useStorage, useMediaQuery } from '@vueuse/core'
import { watch, computed } from 'vue'

export type ThemeMode = 'auto' | 'light' | 'dark'

export interface ThemeConfig {
  primaryColor: string;
  secondaryColor: string;
  brandName: string;
  logoUrl?: string; // Logo URL from project settings (CDN URL)
  loginBackground: {
    fromColor: string;
    toColor: string;
    gridColor: string;
    nodesColor: string;
    nodeCount: number;
    variant?: string;
  };
}

// Gold CSS variable values for firegold preset
const goldCssVars: Record<string, string> = {
  '--color-primary': '#F5B800',
  '--color-primary-50': '#FFF9E6',
  '--color-primary-100': '#FFF0BF',
  '--color-primary-200': '#FFE080',
  '--color-primary-300': '#FFD040',
  '--color-primary-400': '#FFC107',
  '--color-primary-500': '#F5B800',
  '--color-primary-600': '#D4A000',
  '--color-primary-700': '#A67D00',
  '--color-primary-800': '#785A00',
  '--color-primary-900': '#4A3700',
  '--color-secondary': '#EF2D2D',
  '--color-secondary-50': '#FEF2F2',
  '--color-secondary-100': '#FEE2E2',
  '--color-secondary-200': '#FBBABA',
  '--color-secondary-300': '#F87171',
  '--color-secondary-400': '#EF4444',
  '--color-secondary-500': '#EF2D2D',
  '--color-secondary-600': '#DC2626',
  '--color-secondary-700': '#B91C1C',
  '--color-secondary-800': '#991B1B',
  '--color-secondary-900': '#7F1D1D',
}

// Default blue CSS variable values
const blueCssVars: Record<string, string> = {
  '--color-primary': '#3b82f6',
  '--color-primary-50': '#eff6ff',
  '--color-primary-100': '#dbeafe',
  '--color-primary-200': '#bfdbfe',
  '--color-primary-300': '#93c5fd',
  '--color-primary-400': '#60a5fa',
  '--color-primary-500': '#3b82f6',
  '--color-primary-600': '#2563eb',
  '--color-primary-700': '#1d4ed8',
  '--color-primary-800': '#1e40af',
  '--color-primary-900': '#1e3a8a',
  '--color-secondary': '#8b5cf6',
  '--color-secondary-50': '#f5f3ff',
  '--color-secondary-100': '#ede9fe',
  '--color-secondary-200': '#ddd6fe',
  '--color-secondary-300': '#c4b5fd',
  '--color-secondary-400': '#a78bfa',
  '--color-secondary-500': '#8b5cf6',
  '--color-secondary-600': '#7c3aed',
  '--color-secondary-700': '#6d28d9',
  '--color-secondary-800': '#5b21b6',
  '--color-secondary-900': '#4c1d95',
}

// CSS variable sets per preset
const presetCssVars: Record<string, Record<string, string>> = {
  default: blueCssVars,
  firegold: goldCssVars,
}

// Theme presets
export const themePresets = {
  default: {
    primaryColor: 'blue',
    secondaryColor: 'blue',
    brandName: 'FIREGOLD',
    loginBackground: {
      fromColor: 'blue-50',
      toColor: 'indigo-50',
      gridColor: 'blue-400',
      nodesColor: 'blue-400',
      nodeCount: 50
    }
  },
  firegold: {
    primaryColor: 'yellow',
    secondaryColor: 'red',
    brandName: 'FIREGOLD',
    loginBackground: {
      fromColor: 'yellow-50',
      toColor: 'red-50',
      gridColor: 'yellow-500',
      nodesColor: 'red-500',
      nodeCount: 60,
      variant: 'firegold'
    }
  }
}

// Default theme configuration
const defaultThemeConfig: ThemeConfig = themePresets.default

// Apply CSS variables when preset changes
function applyCssVars(presetName: string) {
  const vars = presetCssVars[presetName]
  if (!vars) return
  const root = document.documentElement
  for (const [key, value] of Object.entries(vars)) {
    root.style.setProperty(key, value)
  }
}

export const useThemeStore = defineStore('theme', () => {
  const themeMode = useStorage<ThemeMode>('theme.mode', 'auto')
  const themeConfig = useStorage<ThemeConfig>('theme.config', defaultThemeConfig)
  const activePreset = useStorage<string>('theme.preset', 'firegold')

  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)')

  const isDark = computed(() => {
    if (themeMode.value === 'auto') {
      return prefersDark.value
    }
    return themeMode.value === 'dark'
  })

  // Computed properties for CSS variables
  const primaryColor = computed(() => themeConfig.value.primaryColor)
  const secondaryColor = computed(() => themeConfig.value.secondaryColor)
  const brandName = computed(() => themeConfig.value.brandName)

  // Apply dark/light class to html element
  watch(
    isDark,
    (value) => {
      document.documentElement.classList.toggle('dark', value)
      document.documentElement.classList.toggle('light', !value)
    },
    { immediate: true }
  )

  // Apply CSS variables on init
  watch(
    activePreset,
    (preset) => {
      applyCssVars(preset)
    },
    { immediate: true }
  )

  function toggleTheme() {
    if (themeMode.value === 'auto') {
      themeMode.value = 'light'
    } else if (themeMode.value === 'light') {
      themeMode.value = 'dark'
    } else {
      themeMode.value = 'auto'
    }
  }

  function setTheme(mode: ThemeMode) {
    themeMode.value = mode
  }

  function updateThemeConfig(newConfig: Partial<ThemeConfig>) {
    themeConfig.value = { ...themeConfig.value, ...newConfig }
  }

  function resetThemeConfig() {
    themeConfig.value = { ...defaultThemeConfig }
  }

  function applyThemePreset(presetName: keyof typeof themePresets) {
    if (themePresets[presetName]) {
      themeConfig.value = { ...themePresets[presetName] }
      activePreset.value = presetName
      applyCssVars(presetName)
    }
  }

  return {
    themeMode,
    themeConfig,
    isDark,
    primaryColor,
    secondaryColor,
    brandName,
    activePreset,
    toggleTheme,
    setTheme,
    updateThemeConfig,
    resetThemeConfig,
    applyThemePreset
  }
})
